import type { FormEvent, RefObject } from "react";
import { useTranslation } from "react-i18next";
import { ArrowUp, Pencil, Square, X } from "lucide-react";
import { CONTROL_EDGE, CONTROL_LIT } from "@majourneys/shared/ui";
import { RADIUS, SHADOW_SOFT, SOFT } from "./soft-tokens";
import { PewterCard } from "../../../shared/ui/pewter-card";

const CONTROL = { background: CONTROL_LIT, boxShadow: `inset 0 0 0 1px ${CONTROL_EDGE}` } as const;

/**
 * The composer (chat kit C18): one field on the pewter band and one round
 * button at its end.
 *
 *   · Empty, the send coin is there but dimmed: it says where to press without
 *     pretending it can be pressed.
 *   · Typed, the coin is the navy ramp with a white arrow.
 *   · While the answer arrives, the coin becomes Stop, which ends the answer
 *     and keeps the thread (`useAiChat().stop`). Hosts that pass no `onStop`
 *     keep the old behaviour: a disabled coin.
 *
 * No suggestion chips above the field. A question the assistant asks carries
 * its own answers on the turn that asked it.
 */
export function ChatComposer({
  value,
  onChange,
  onSubmit,
  loading,
  inputRef,
  compact = false,
  onStop,
  editing = false,
  onCancelEdit,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: (e: FormEvent) => void;
  loading: boolean;
  inputRef: RefObject<HTMLInputElement | null>;
  compact?: boolean;
  /** Ends the answer that is arriving. Shown as Stop while `loading`. */
  onStop?: () => void;
  /** The field holds an earlier message being edited (chat kit C2). */
  editing?: boolean;
  onCancelEdit?: () => void;
}) {
  const { t } = useTranslation();
  const has = !!value.trim();
  const streaming = loading;
  return (
    <div
      style={{
        background: SOFT.ground,
        padding: compact ? "8px 12px 12px" : "8px 16px 16px",
      }}
    >
      {editing ? (
        <div
          className="mj-fade-in mb-1.5 flex items-center gap-2 px-2 text-[12px]"
          style={{ color: SOFT.body }}
          data-testid="composer-editing"
        >
          <Pencil size={12} aria-hidden="true" />
          <span className="min-w-0 flex-1 break-words">
            {t("ai_concierge.turn.editing", { defaultValue: "Editing your message" })}
          </span>
          {onCancelEdit ? (
            <button
              type="button"
              onClick={onCancelEdit}
              className="inline-flex min-h-[24px] shrink-0 items-center gap-1 rounded-full px-2 font-semibold"
              style={{ color: SOFT.ink }}
            >
              <X size={12} aria-hidden="true" />
              {t("ai_concierge.turn.edit_cancel", { defaultValue: "Cancel" })}
            </button>
          ) : null}
        </div>
      ) : null}
      <PewterCard
        radius={RADIUS.pill}
        fill={SOFT.surface}
        style={{ boxShadow: SHADOW_SOFT }}
        innerClassName="flex items-center gap-2"
        innerStyle={{ paddingInlineStart: 16, paddingInlineEnd: 5, paddingBlock: 5 }}
      >
        <form onSubmit={onSubmit} className="flex flex-1 items-center gap-2">
          <input
            ref={inputRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={streaming}
            placeholder={
              streaming
                ? t("ai_concierge.turn.answering", { defaultValue: "MaJourneys AI is answering" })
                : t("ai_concierge.placeholder_v3", { defaultValue: "Ask about your trip…" })
            }
            className="min-h-[38px] min-w-0 flex-1 bg-transparent text-[15px] leading-5 outline-none placeholder:text-[#5a5d77] disabled:cursor-not-allowed"
            style={{ color: SOFT.ink }}
          />
          {streaming && onStop ? (
            <button
              type="button"
              onClick={onStop}
              aria-label={t("ai_concierge.turn.stop", { defaultValue: "Stop" })}
              className="mj-press grid h-[38px] w-[38px] shrink-0 place-items-center"
              style={{ ...CONTROL, borderRadius: RADIUS.pill, color: SOFT.ink }}
              data-testid="composer-stop"
            >
              <Square size={12} fill={SOFT.ink} aria-hidden="true" />
            </button>
          ) : (
            <button
              type="submit"
              disabled={streaming || !has}
              aria-label={t("ai_concierge.send", { defaultValue: "Ask" })}
              className="mj-press grid h-[38px] w-[38px] shrink-0 place-items-center transition-opacity disabled:cursor-not-allowed"
              style={{
                borderRadius: RADIUS.pill,
                background: SOFT.buttonFace,
                color: "#fff",
                opacity: has && !streaming ? 1 : 0.4,
                boxShadow: has ? `0 10px 24px -12px rgba(17,14,57,0.8), ${SOFT.buttonInset}` : SOFT.buttonInset,
              }}
              data-testid="composer-send"
            >
              <ArrowUp size={17} aria-hidden="true" />
            </button>
          )}
        </form>
      </PewterCard>
    </div>
  );
}
